import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics/api';
import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';

import type { AcceptSuggestionParams } from '../../aiAutocompletePluginType';
import { ACTIONS } from '../types/types';
import {
	addAutocompleteAcceptedAnalytics,
	addAutocompleteRejectedAnalytics,
} from '../utils/analytics';
import { aiAutocompletePluginKey } from './plugin-key';

/**
 * Returns an EditorCommand that inserts the suggestion at cursorPos and dismisses.
 * Caller must pass cursorPos and suggestion (e.g. from plugin state when status === 'suggestion').
 * Used when the user presses Tab.
 */
export const acceptSuggestionCommand =
	(
		{ cursorPos, suggestion }: AcceptSuggestionParams,
		editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	): EditorCommand =>
	({ tr }) => {
		addAutocompleteAcceptedAnalytics(editorAnalyticsAPI)(tr);
		tr.insert(cursorPos, tr.doc.type.schema.text(suggestion));
		tr.setSelection(TextSelection.create(tr.doc, cursorPos + suggestion.length));
		tr.setMeta(aiAutocompletePluginKey, { type: ACTIONS.DISMISS });
		tr.setMeta('addToHistory', true);
		return tr;
	};

/**
 * EditorCommand that dismisses the current suggestion without inserting anything.
 * Used for Escape key, blur, and other dismiss triggers.
 */
export const dismissSuggestionCommand =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined): EditorCommand =>
	({ tr }) => {
		addAutocompleteRejectedAnalytics(editorAnalyticsAPI)(tr);
		return tr.setMeta(aiAutocompletePluginKey, { type: ACTIONS.DISMISS });
	};
